import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CalendarOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Col,
  Row,
  Space,
  Typography,
  DatePicker,
  Spin,
  Empty,
  Card,
} from "antd";

import dayjs from "../../../config/dayjs";
import { getUser } from "../../../services/auth";
import { capitalizeEachWord } from "../../../utils/stringUtils";
import {
  getAdminDashboard,
  getLawyerDashboard,
  getClientDashboard,
  getAgentDashboard,
  getStaffDashboard,
  getBranchAdminDashboard,
} from "../api/dashboardAPI";
import AdminDashboardCards from "../components/adminDashboard";
import AgentDashboardCards from "../components/agentDashboard";
import BranchAdminDashboardCards from "../components/branchAdminDashboard";
import ClientDashboardCards from "../components/clientDashboard";
import LawyerDashboardCards from "../components/lawyerDashboard";
import StaffDashboardCards from "../components/staffDashboard";
import { styles } from "../styles/dashboardStyles";

const Dashboard = () => {
  const { Title, Text } = Typography;
  const { t } = useTranslation();
  const user = getUser();
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>(
    undefined
  );
  const [selectedFileStatusDate, setSelectedFileStatusDate] = useState(
    dayjs().format("YYYY-MM")
  );

  // Format dates for API
  const getFormattedDate = () => selectedMonth.format("YYYY-MM-DD");

  // features for seremban branch
  const isSerembanBranch = user.branch?.branchName === "Seremban";

  const { data: dashboard, isLoading: isDashboardLoading } = useQuery({
    queryKey: [
      "dashboard",
      selectedMonth.format("YYYY-MM"),
      selectedYear,
      selectedFileStatusDate,
      selectedBranchId,
    ],
    queryFn: () => {
      const date = getFormattedDate();

      switch (user.role) {
        case "admin":
          return getAdminDashboard({
            date,
            spaPriceDate: selectedYear,
            spaPriceBranchId: selectedBranchId,
          });
        case "branchAdmin":
          return getBranchAdminDashboard({
            date,
            branchAdminId: user.id,
            spaPriceDate: selectedYear,
          });
        case "lawyer":
          return getLawyerDashboard({
            date,
            lawyerId: user.id,
          });
        case "client":
          return getClientDashboard({ clientId: user.id });
        case "agent":
          return getAgentDashboard({
            agentId: user.id,
            spaPriceDate: selectedYear,
            fileStatusDate: selectedFileStatusDate,
          });
        default:
          return getStaffDashboard({ staffId: user.id });
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Render dashboard cards based on user role
  const renderDashboardCards = () => {
    if (isDashboardLoading) {
      return (
        <Col span={24} style={{ textAlign: "center", padding: 20 }}>
          <Spin size="large" fullscreen />
        </Col>
      );
    }

    if (!dashboard) {
      return (
        <Col span={24}>
          <Empty description="No data available" />
        </Col>
      );
    }

    switch (user.role) {
      case "admin":
        return (
          <AdminDashboardCards
            dashboard={{
              ...dashboard,
              month: selectedMonth.format("MMMM"),
            }}
            selectedBranchId={selectedBranchId}
            onBranchIdChange={(branchId) => setSelectedBranchId(branchId)}
            selectedYear={selectedYear}
            onYearChange={(year) => setSelectedYear(year)}
          />
        );
      case "branchAdmin":
        return (
          <BranchAdminDashboardCards
            dashboard={{
              ...dashboard,
              month: selectedMonth.format("MMMM"),
            }}
            isSerembanBranch={isSerembanBranch}
            selectedYear={selectedYear}
            onYearChange={(year) => setSelectedYear(year)}
          />
        );
      case "lawyer":
        return (
          <LawyerDashboardCards
            dashboard={{
              ...dashboard,
              month: selectedMonth.format("MMMM"),
            }}
          />
        );
      case "staff":
        return <StaffDashboardCards dashboard={dashboard} />;
      case "client":
        return <ClientDashboardCards dashboard={dashboard} />;
      case "agent":
        return (
          <AgentDashboardCards
            dashboard={dashboard}
            isSerembanBranch={isSerembanBranch}
            selectedYear={selectedYear}
            selectedFileStatusDate={selectedFileStatusDate}
            onYearChange={(year) => setSelectedYear(year)}
            onFileStatusDateChange={(date) => setSelectedFileStatusDate(date)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <Card bordered={false}>
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 20 }}>
          <Col xs={24} md={12}>
            <Space direction="vertical" size="middle">
              <Title level={4} style={styles.title}>
                {t("RootLayout.hello", {
                  name: capitalizeEachWord(user.name),
                })}
              </Title>
              <Text>
                Here's your performance overview for{" "}
                {selectedMonth.format("MMMM YYYY")}
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: "right" }}>
            {(user.role === "lawyer" ||
              user.role === "admin" ||
              user.role === "branchAdmin") && (
              <Space align="center">
                <Text type="secondary">Select month:</Text>
                <DatePicker
                  picker="month"
                  value={selectedMonth}
                  onChange={(date) => setSelectedMonth(date)}
                  allowClear={false}
                  format="MMMM YYYY"
                  style={{
                    width: 160,
                    borderRadius: "6px",
                  }}
                  suffixIcon={<CalendarOutlined style={{ color: "#1890ff" }} />}
                />
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>{renderDashboardCards()}</Row>
    </div>
  );
};

export default Dashboard;
